import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { AlertCircle, Shield } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const hasRequiredRole = (userRole, requiredRoles) => {
  if (!requiredRoles || requiredRoles.length === 0) {
    return true; // No role requirement
  }
  
  return requiredRoles.includes(userRole);
};

export const ProtectedRoute = ({ 
  children, 
  requiredRoles = [], 
  redirectTo = '/signin',
  fallback = null 
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Not authenticated - redirect to login, preserving the page the user was
  // trying to reach (e.g. an email deep-link to /provider/projects/123) so
  // SigninPage can send them back there after a successful login instead of
  // dumping them on the dashboard.
  if (!isAuthenticated || !user) {
    const intended = `${location.pathname}${location.search}${location.hash}`;
    const target = intended && intended !== '/'
      ? `${redirectTo}?redirect=${encodeURIComponent(intended)}`
      : redirectTo;
    return <Navigate to={target} replace />;
  }

  // Check if user has required role
  if (!hasRequiredRole(user.role, requiredRoles)) {
    // Show access denied page instead of redirecting
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              You don't have permission to access this page. 
              {requiredRoles.length > 0 && (
                <>
                  <br />
                  Required role(s): {requiredRoles.join(', ')}
                </>
              )}
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Shield className="w-4 h-4" />
              <span>Your role: {user.role}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is authenticated and authorized
  return children || fallback;
};

// Higher-order component for easier usage
export const withAuth = (Component, requiredRoles = []) => {
  return (props) => (
    <ProtectedRoute requiredRoles={requiredRoles}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

// Utility functions that work with the existing auth system
// Note: These should be used within components that have access to the auth context
// For standalone usage, consider using the authService directly

// Higher-order component to inject auth utilities
export const withAuthUtils = (Component) => {
  return (props) => {
    const { user, isAuthenticated } = useAuth();
    
    const authUtils = {
      isAdmin: isAuthenticated && user?.role === 'admin',
      isProvider: isAuthenticated && user?.role === 'provider', 
      isCustomer: isAuthenticated && user?.role === 'customer',
      hasRole: (role) => isAuthenticated && user?.role === role,
      user,
      isAuthenticated
    };
    
    return <Component {...props} authUtils={authUtils} />;
  };
};